import {Injectable} from "@angular/core";
import {Observable, Subject} from "rxjs";
import {INSPIRE_VIDEOS_URL} from "../../../common";

export interface IInspireVideo {
    /** Bare file name without extension, e.g. "bank_lobby". Cache key. */
    file: string;
    /** Full https url to the mp4 (Express /_videos route, CORS-enabled). */
    url: string;
    /** Same url with a `#t=` media fragment so a <video> seeks to and
     *  paints the FIRST FRAME without playing — used to capture the
     *  thumbnail image. */
    frameUrl: string;
    /** Human label shown on the card — alphanumeric + spaces only. */
    label: string;
    /** Material icon hinting at the industry — fallback when no frame. */
    icon: string;
}

/**
 * Shared state + first-frame cache for the home "Get inspired" videos.
 *
 * WHY A SERVICE: the strip and the dialog filmstrip render the SAME clips
 * via the SAME <app-inspire-thumb>. Holding the video list and the captured
 * thumbnails here (one root singleton) means one source of truth AND one
 * thumbnail cache — a frame captured for the strip is reused instantly in
 * the dialog (and vice versa), no re-fetch / re-decode.
 *
 * HOW THUMBS ARE MADE: thumbs themselves never load a <video> — they are
 * pure <img>/icon. ALL video loading happens here, in a concurrency-limited
 * capture engine: at most MAX_CONCURRENT offscreen <video>s decode their
 * first frame to a canvas at once, the rest queue. Visible thumbs request
 * priority so they fill first; `prefetchThumbs()` enqueues the rest on idle.
 * `thumbCaptured$` notifies thumbs when their frame lands.
 *
 * Canvas capture only works because the Express /_videos route sends CORS
 * headers and the <video> is `crossorigin="anonymous"` (canvas not tainted).
 */
@Injectable({providedIn: "root"})
export class HomeInspireService {
    /** Max offscreen capture videos decoding at once — keeps first paint
     *  light even though every card/thumb is in the DOM. */
    private static readonly MAX_CONCURRENT = 4;

    /** Maps a keyword found in the file name to a decorative icon. First
     *  match wins; anything unmatched falls back to a generic play icon. */
    private static readonly ICON_HINTS: Array<[RegExp, string]> = [
        [/bank/, "account_balance"],
        [/barber/, "content_cut"],
        [/car/, "directions_car"],
        [/church/, "church"],
        [/coffee/, "local_cafe"],
        [/factory/, "factory"],
        [/fashion/, "checkroom"],
        [/gym/, "fitness_center"],
        [/hospital/, "local_hospital"],
        [/movie|theater/, "theaters"],
        [/pet/, "pets"],
        [/pharmacy/, "local_pharmacy"],
        [/public|services/, "account_balance"],
        [/qsr|menu/, "restaurant"],
        [/real_estate/, "home"],
        [/spa/, "spa"],
        [/supermarket/, "shopping_cart"],
        // Hints for the 2026 06 08 batch (appended — first match wins, so
        // these never override an earlier industry's icon).
        [/apartment|residential/, "apartment"],
        [/brewery|taproom/, "sports_bar"],
        [/casino/, "casino"],
        [/convention|conference/, "groups"],
        [/metro|transit|station/, "train"],
        [/nail|salon/, "spa"],
        [/office|break_room/, "business_center"],
        [/restaurant|dining/, "restaurant"],
        [/senior|living/, "weekend"],
        [/veterinary|vet/, "pets"],
    ];

    /** The full bank of intro clips. Keep file names in sync with the
     *  folder served by the Express /_videos route — adding a new mp4
     *  there means just adding its bare name here. */
    private static readonly FILES: string[] = [
        "bank_lobby",
        "barber_shop_entrance",
        "barber_shop",
        "car_showroom_1",
        "church_lobby",
        "coffee_shop",
        "factory_floor",
        "fashion_boutique_1",
        "gym_floor_1",
        "hospital_atrium_1",
        "movie_theater_lobby",
        "pet_grooming_appointments",
        "pet_salon_display",
        "pharmacy_counter",
        "public_services_office",
        "qsr_menu_board_1",
        "real_estate_window",
        "spa_reception",
        "supermarket_aisle",

        // New industries added 2026 06 08. Keep names short + snake_case, and
        // add the SAME bare name to digitalsignage.com index.html VIDEOS array.
        // Full workflow: ADDING-INTRO-VIDEOS.md in the home/ folder.
        "apartment_lobby",
        "brewery_taproom",
        "casino_floor",
        "convention_center",
        "metro_station",
        "nail_salon",
        "office_break_room",
        "restaurant_dining",
        "senior_living_lounge",
        "veterinary_clinic",
    ];

    readonly videos: IInspireVideo[] = HomeInspireService.FILES.map((file) => {
        const url = `${INSPIRE_VIDEOS_URL}/${file}.mp4`;
        return {
            file,
            url,
            frameUrl: `${url}#t=0.1`,
            label: file.replace(/_/g, " "),
            icon: HomeInspireService.iconFor(file),
        };
    });

    /** file -> objectURL of the captured first frame. App-lifetime cache;
     *  the blobs are tiny jpegs so we never revoke them. */
    private readonly thumbCache = new Map<string, string>();
    /** Emits a clip's file name right after its thumbnail is cached. */
    private readonly captured = new Subject<string>();
    /** Public stream thumbs subscribe to (filtered by their own file). */
    readonly thumbCaptured$: Observable<string> = this.captured.asObservable();

    /** Capture engine state. `queued` dedupes the work-list; `queue` is the
     *  ordered backlog; `active` counts in-flight captures. */
    private readonly queued = new Set<string>();
    private readonly queue: string[] = [];
    private active = 0;
    private prefetchStarted = false;

    private static iconFor(file: string): string {
        for (const [re, icon] of HomeInspireService.ICON_HINTS) {
            if (re.test(file)) return icon;
        }
        return "play_circle";
    }

    /** Cached thumbnail objectURL for a clip, or undefined if not captured. */
    getThumb(file: string): string | undefined {
        return this.thumbCache.get(file);
    }

    /** Queue a clip's first-frame capture. Idempotent — already cached or
     *  already queued is a no-op. `priority` (a thumb that's actually on
     *  screen) jumps the queue ahead of the idle prefetch backlog. */
    ensureThumb(file: string, priority = false): void {
        if (this.thumbCache.has(file) || this.queued.has(file)) return;
        this.queued.add(file);
        if (priority) {
            this.queue.unshift(file);
        } else {
            this.queue.push(file);
        }
        this.pump();
    }

    /** Warm the cache in the background so both the strip and the dialog
     *  fill in fast. Runs once, on browser idle; the concurrency cap in
     *  pump() keeps it from competing with the initial page load. */
    prefetchThumbs(): void {
        if (this.prefetchStarted) return;
        this.prefetchStarted = true;
        const start = () => this.videos.forEach((v) => this.ensureThumb(v.file, false));
        const w = window as any;
        if (typeof w.requestIdleCallback === "function") {
            w.requestIdleCallback(start, {timeout: 4000});
        } else {
            setTimeout(start, 1500);
        }
    }

    /** Drain the queue up to the concurrency cap. */
    private pump(): void {
        while (this.active < HomeInspireService.MAX_CONCURRENT && this.queue.length) {
            const file = this.queue.shift()!;
            this.active++;
            this.captureFile(file).finally(() => {
                this.active--;
                this.queued.delete(file);
                this.pump();
            });
        }
    }

    /** Load one clip's first frame in an offscreen <video> and capture it.
     *  Resolves when the slot is free again (success or failure). */
    private captureFile(file: string): Promise<void> {
        const v = this.videos.find((x) => x.file === file);
        if (!v) return Promise.resolve();

        return new Promise<void>((resolve) => {
            const video = document.createElement("video");
            video.crossOrigin = "anonymous";
            video.muted = true;
            video.preload = "metadata";
            (video as any).playsInline = true;

            // Each capture settles exactly once. loadeddata AND seeked can
            // both fire and the capture is async, so guard against re-entry.
            let capturing = false;
            let settled = false;
            const finish = () => {
                if (settled) return;
                settled = true;
                video.removeEventListener("loadeddata", onData);
                video.removeEventListener("seeked", onData);
                video.removeEventListener("error", onErr);
                video.removeAttribute("src");
                video.load();
                resolve();
            };
            const onData = () => {
                if (settled || capturing) return;
                capturing = true;
                this.captureFrame(video, file).then((url) => {
                    if (url) this.captured.next(file);
                    finish();
                });
            };
            const onErr = () => finish();

            video.addEventListener("loadeddata", onData);
            video.addEventListener("seeked", onData);
            video.addEventListener("error", onErr);
            video.src = v.frameUrl;
            video.load();
        });
    }

    /** Draw the current frame of a (CORS-enabled) <video> to a canvas and
     *  cache it as a jpeg objectURL. Resolves with the url, or null if the
     *  frame isn't ready / the canvas is tainted. Idempotent per file. */
    private captureFrame(video: HTMLVideoElement, file: string): Promise<string | null> {
        const existing = this.thumbCache.get(file);
        if (existing) return Promise.resolve(existing);

        return new Promise<string | null>((resolve) => {
            try {
                const w = video.videoWidth;
                const h = video.videoHeight;
                if (!w || !h) {
                    resolve(null);
                    return;
                }
                const canvas = document.createElement("canvas");
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    resolve(null);
                    return;
                }
                ctx.drawImage(video, 0, 0, w, h);
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            resolve(null);
                            return;
                        }
                        const won = this.thumbCache.get(file);
                        if (won) {
                            resolve(won);
                            return;
                        }
                        const objectUrl = URL.createObjectURL(blob);
                        this.thumbCache.set(file, objectUrl);
                        resolve(objectUrl);
                    },
                    "image/jpeg",
                    0.72,
                );
            } catch {
                // Tainted canvas (CORS) or other failure — give up quietly.
                resolve(null);
            }
        });
    }
}
